import React, { useState } from 'react'
import * as R from 'ramda'
import PlacesAutocomplete, { geocodeByPlaceId } from 'react-places-autocomplete'
import { Box, Text } from '@myxplor/stardust'

import googleimg from '~/images/powered_by_google_default.png'
import './placesWrapper.css'

const TextLabel = ({ children }) => {
  return (
    <Text fontSize="16px" weight="regular">
      {children}
    </Text>
  )
}

const PlacesWrapper = ({ question }) => {
  const [address, setAddress] = useState('')
  const [error, setError] = useState(null)
  const [fullAddress, setFullAddress] = useState({
    unitNumber: question.value ? question.value.unitNumber : '',
    streetNumber: question.value ? question.value.streetNumber : '',
    street: question.value ? question.value.street : '',
    suburb: question.value ? question.value.suburb : '',
    state: question.value ? question.value.state : '',
    stateLongName: question.value ? question.value.stateLongName : '',
    postcode: question.value ? question.value.postcode : '',
    country: question.value ? question.value.country : ''
  })

  const blankAddress = {
    unitNumber: '',
    streetNumber: '',
    street: '',
    suburb: '',
    state: '',
    stateLongName: '',
    postcode: '',
    country: ''
  }

  const clearAddress = () => {
    question.value = blankAddress
    setFullAddress(blankAddress)
    setAddress('')
  }

  const extractAddressField = (address_components, type, field) => {
    return R.pipe(
      R.find(R.propSatisfies(locationType => locationType[0] === type, 'types')),
      R.prop(field),
      R.defaultTo('')
    )(address_components)
  }

  const handleUnitNumberChange = e => {
    e.preventDefault()
    setFullAddress({ ...fullAddress, unitNumber: e.target.value })
  }

  const handleError = errorCode => {
    let humanError = errorCode

    switch (errorCode) {
      case 'ZERO_RESULTS': {
        humanError = 'Provided address could not be found. Please check that it has been entered correctly'
        break
      }
      case 'INVALID_REQUEST': {
        humanError = 'Please enter a valid address.'
        break
      }
      default: {
        humanError = 'Address validation failed. Please check that it has been entered correctly.'
        break
      }
    }

    setError(humanError)
    clearAddress()
  }

  const handleSelect = (address, placeId) => {
    geocodeByPlaceId(placeId)
      .then(resp => resp[0].address_components)
      .then(address_components => {
        const ukPostalTown = extractAddressField(address_components, 'postal_town', 'long_name')
        const locality = extractAddressField(address_components, 'locality', 'long_name')
        const fullAddressSetter = {
          streetNumber: extractAddressField(address_components, 'street_number', 'short_name'),
          street: extractAddressField(address_components, 'route', 'short_name'),
          suburb: locality ? locality : ukPostalTown,
          state: extractAddressField(address_components, 'administrative_area_level_1', 'short_name'),
          stateLongName: extractAddressField(address_components, 'administrative_area_level_1', 'long_name'),
          postcode: extractAddressField(address_components, 'postal_code', 'short_name'),
          country: extractAddressField(address_components, 'country', 'long_name'),
          unitNumber: fullAddress.unitNumber
        }
        question.value = fullAddressSetter
        setFullAddress(fullAddressSetter)
        setError(null)
      })
      .catch(error => {
        handleError(error)
      })
    setAddress(address)
  }

  return (
    <>
      <Box paddingBottom="xsmall">
        <PlacesAutocomplete
          onChange={setAddress}
          value={address}
          onSelect={handleSelect}
          onError={handleError}
          shouldFetchSuggestions={address.length > 4}
          debounce={1000}
        >
          {({ getInputProps, suggestions, getSuggestionItemProps }) => {
            return (
              <div className="autosuggest-container">
                {!question.isReadOnly && (
                  <Box display="flex" width={1} flexWrap="wrap">
                    <Box width={[1, 1 / 8]} paddingRight={['none', 'small']}>
                      <TextLabel>Unit #</TextLabel>
                      <input value={fullAddress.unitNumber} onChange={handleUnitNumberChange} />
                    </Box>
                    <Box width={[1, 7 / 8]} className="autosuggest-input">
                      <TextLabel>Street Address</TextLabel>
                      <div className="autosuggest-input">
                        <input
                          {...getInputProps({
                            placeholder: 'Search Places...',
                            className: 'autosuggest-search-input'
                          })}
                        />
                      </div>
                    </Box>
                  </Box>
                )}

                {suggestions.length > 0 && (
                  <Box border="1px #6d7072 solid" className="suggestions-container">
                    {suggestions.map(suggestion => {
                      const className = suggestion.active ? 'suggestion-item-active' : 'suggestion-item'
                      return (
                        /* eslint-disable react/jsx-key */
                        <Box
                          padding="xsmall"
                          paddingLeft="small"
                          {...getSuggestionItemProps(suggestion, { className })}
                        >
                          <Text fontSize="14px">
                            {suggestion.formattedSuggestion.mainText}{' '}
                            {suggestion.formattedSuggestion.secondaryText}
                          </Text>
                        </Box>
                      )
                      /* eslint-enable react/jsx-key */
                    })}

                    <Box padding="xxxsmall" className="autosuggest-footer">
                      <div>
                        <img width="150px" src={googleimg} alt="powered by Google" />
                      </div>
                    </Box>
                  </Box>
                )}
              </div>
            )
          }}
        </PlacesAutocomplete>

        {error && (
          <Box
            border="1px solid #ed5565"
            backgroundColor="background"
            padding="xsmall"
            marginTop="xsmall"
            className="autosuggest-error"
          >
            <Text fontSize="14px" color="#ed5565">
              {error}
            </Text>
          </Box>
        )}
      </Box>
      <Box>
        {(fullAddress.country !== '' || question.isReadOnly) && (
          <div className="address-fields">
            <Box display="flex" width={1} flexWrap="wrap">
              <Box width={[1, 1 / 8]} paddingRight={['none', 'small']} paddingBottom="small">
                <TextLabel>Unit #</TextLabel>
                <input disabled value={fullAddress.unitNumber} />
              </Box>
              <Box width={[1, 1 / 8]} paddingRight={['none', 'small']} paddingBottom="small">
                <TextLabel>Street #</TextLabel>
                <input disabled value={fullAddress.streetNumber} />
              </Box>
              <Box width={[1, 4 / 8]} paddingRight={['none', 'small']} paddingBottom="small">
                <TextLabel>Street</TextLabel>
                <input disabled value={fullAddress.street} />
              </Box>
              <Box width={[1, 2 / 8]} paddingBottom="small">
                <TextLabel>Suburb</TextLabel>
                <input disabled value={fullAddress.suburb} />
              </Box>
              <Box width={[1, 1 / 4]} paddingRight={['none', 'small']} paddingBottom="small">
                <TextLabel>State</TextLabel>
                <input disabled value={fullAddress.state} />
              </Box>
              <Box width={[1, 1 / 4]} paddingRight={['none', 'small']} paddingBottom="small">
                <TextLabel>Post Code</TextLabel>
                <input disabled value={fullAddress.postcode} />
              </Box>
              <Box width={[1, 1 / 2]} paddingBottom="small">
                <TextLabel>Country</TextLabel>
                <input disabled value={fullAddress.country} />
              </Box>
              <Text
                onClick={clearAddress}
                style={{
                  textDecoration: 'underline',
                  cursor: 'pointer'
                }}
              >
                Clear address
              </Text>
            </Box>
          </div>
        )}
      </Box>
    </>
  )
}

export default PlacesWrapper
